import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";
import { rewampPalette } from "../theme";

// The four consumers all render under the rewamp ThemeProvider, but this
// component is generic enough to be dropped under the legacy theme (the
// OtpInputRenderer precedent), where `theme.controls` is absent — so fall back
// to the same token object rather than crash on a hard property access.
const controlsOf = (theme) => theme.controls ?? rewampPalette.controls;

// `fitHeight` (px) squares the button off against the measured row instead of
// the fixed token size, so the circle matches the strip it sits in.
const arrowSxBase = (surface, fitHeight) => (theme) => {
  const size = fitHeight ? `${fitHeight}px` : theme.spacing(controlsOf(theme).scrollArrowSize);
  return {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    width: size,
    height: size,
    backgroundColor: surface,
    color: theme.palette.primary.interactive,
    border: `1px solid ${theme.palette.neutral.stroke}`,
    boxShadow: theme.shadows[2],
    "&:hover": { backgroundColor: theme.palette.primary.softSurface },
  };
};

const RewampScrollRow = ({
  children,
  rowProps = {},
  scrollerSx = {},
  rowSx = {},
  arrowSx = {},
  activeDescendantId,
  arrowFit = false,
  arrowSurface = "neutral.surface",
  fitContent = false,
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const rowRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [rowHeight, setRowHeight] = useState(0);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < maxScroll - 1);
    if (rowRef.current) setRowHeight(rowRef.current.offsetHeight);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    const row = rowRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);
    // The inner row (`minWidth: max-content`) is what actually changes size as
    // web fonts load and labels reflow — the scroller's own box stays 100%
    // wide, so a ResizeObserver on IT would never fire for that.
    const observer = row && window.ResizeObserver ? new ResizeObserver(updateScrollState) : null;
    observer?.observe(row);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
      observer?.disconnect();
    };
  }, [updateScrollState]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !activeDescendantId) return;
    const item = el.querySelector(`#${CSS.escape(activeDescendantId)}`);
    if (!item) return;
    // Only chase the active item when it is actually out of view — an
    // unconditional scrollIntoView re-centres (and nudges the page) on mount.
    const view = el.getBoundingClientRect();
    const box = item.getBoundingClientRect();
    if (box.left < view.left || box.right > view.right) {
      item.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activeDescendantId]);

  const scrollByViewport = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.6, behavior: "smooth" });
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "flex-start",
        width: fitContent ? "fit-content" : "100%",
        maxWidth: "100%",
      }}
    >
      {canScrollLeft && (
        <IconButton
          onClick={() => scrollByViewport(-1)}
          aria-label={t("COMMON_SCROLL_LEFT")}
          size="small"
          sx={[arrowSxBase(arrowSurface, arrowFit ? rowHeight : 0), { left: 0 }, arrowSx]}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      )}
      <Box
        ref={scrollRef}
        data-testid="rewamp-scroll-row"
        sx={[
          {
            display: "flex",
            width: "100%",
            overflowX: "auto",
            scrollBehavior: "smooth",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          },
          scrollerSx,
          // After scrollerSx so a consumer's own `p`/`px` can't cancel the
          // gutter that keeps the first/last item clear of the arrows. Each
          // side's gutter tracks only its own arrow (canScrollLeft/Right),
          // so no gutter is reserved for a hidden arrow.
          (canScrollLeft || canScrollRight) &&
            ((theme) => {
              const gutter =
                arrowFit && rowHeight
                  ? `${rowHeight + 8}px`
                  : theme.spacing(controlsOf(theme).scrollArrowGutter);
              return {
                ...(canScrollLeft && {
                  paddingInlineStart: gutter,
                  scrollPaddingInlineStart: gutter,
                }),
                ...(canScrollRight && {
                  paddingInlineEnd: gutter,
                  scrollPaddingInlineEnd: gutter,
                }),
              };
            }),
        ]}
      >
        <Box
          ref={rowRef}
          {...rowProps}
          sx={[{ display: "flex", alignItems: "center", minWidth: "max-content" }, rowSx]}
        >
          {children}
        </Box>
      </Box>
      {canScrollRight && (
        <IconButton
          onClick={() => scrollByViewport(1)}
          aria-label={t("COMMON_SCROLL_RIGHT")}
          size="small"
          sx={[arrowSxBase(arrowSurface, arrowFit ? rowHeight : 0), { right: 0 }, arrowSx]}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

RewampScrollRow.propTypes = {
  children: PropTypes.node,
  /** Spread onto the inner row — put role/aria-label here, not on the scroller. */
  rowProps: PropTypes.object,
  /** Merged into the scroll container (border, background, vertical padding). */
  scrollerSx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
  /** Merged into the inner row (gap, alignItems). */
  rowSx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
  /** Merged into both arrows — mainly to re-anchor `top` off the row's centre. */
  arrowSx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
  /** DOM id of the selected item; scrolled into view when it changes and is off-screen. */
  activeDescendantId: PropTypes.string,
  /** Size the arrows off the measured row height rather than the fixed token. */
  arrowFit: PropTypes.bool,
  /** Palette path for the arrow fill — pass the strip's own surface to blend in. */
  arrowSurface: PropTypes.string,
  /** Shrink the outer wrapper to its content width instead of stretching to 100%. */
  fitContent: PropTypes.bool,
};

export default RewampScrollRow;
